import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  CircularProgress,
  Alert,
  Link,
  Paper
} from '@mui/material';
import { signUp } from '../../services/authService';

interface SignUpFormProps {
  onSignIn: () => void;
  onSuccess?: () => void;
}

const SignUpForm: React.FC<SignUpFormProps> = ({ onSignIn, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const isValidInput = 
    email.includes('@') && 
    password.length >= 6 && 
    password === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValidInput) return;
    
    setIsLoading(true);
    setError(null);

    const result = await signUp(email, password);
    
    setIsLoading(false);
    
    if (result.success) {
      setSuccess(true);
      if (onSuccess) {
        onSuccess();
      }
    } else {
      setError(result.error || 'Failed to create account');
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 400, mx: 'auto', mt: 8 }}>
      <Typography variant="h4" component="h1" align="center" gutterBottom>
        Create Account
      </Typography>
      
      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Account created successfully! You can now sign in.
          </Alert>
        )}
        
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={email !== '' && !email.includes('@')}
          helperText={email !== '' && !email.includes('@') ? 'Enter a valid email' : ''}
          disabled={isLoading || success}
        />
        
        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label="Password"
          type="password"
          id="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={password !== '' && password.length < 6}
          helperText={password !== '' && password.length < 6 ? 'Password must be at least 6 characters' : ''}
          disabled={isLoading || success}
        />
        
        <TextField
          margin="normal"
          required
          fullWidth
          name="confirmPassword"
          label="Confirm Password"
          type="password"
          id="confirmPassword"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={confirmPassword !== '' && password !== confirmPassword}
          helperText={confirmPassword !== '' && password !== confirmPassword ? 'Passwords do not match' : ''}
          disabled={isLoading || success}
        />
        
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2, py: 1.5 }}
          disabled={isLoading || !isValidInput || success}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Sign Up'}
        </Button>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Link 
            component="button" 
            variant="body2" 
            onClick={onSignIn} 
            sx={{ cursor: 'pointer' }}
          >
            {"Already have an account? Sign In"}
          </Link>
        </Box>
      </Box>
    </Paper>
  );
};

export default SignUpForm; 